/** @format */
import { MarketCode, MarketLanguage, MarketSlug } from '@mpb-com/feather/enums';
import { Operation, OperationId } from '@mpb-com/feather/enums/operation';

export { MarketCode, MarketLanguage, MarketSlug, Operation, OperationId };

export enum MarketIso {
    en_GB = 'en_GB',
    en_EU = 'en_EU',
    en_US = 'en_US',
    en_IE = 'en_IE',
    fr_FR = 'fr_FR',
    fr_BE = 'fr_BE',
    nl_BE = 'nl_BE',
    nl_NL = 'nl_NL',
    de_DE = 'de_DE',
    de_AT = 'de_AT',
    it_IT = 'it_IT',
    es_ES = 'es_ES',
    pt_PT = 'pt_PT',
}

export enum ContentLanguageFallbacks {
    en_EU = MarketIso.en_GB,
    en_IE = MarketIso.en_GB,
    fr_BE = MarketIso.fr_FR,
    nl_BE = MarketIso.nl_NL,
    de_AT = MarketIso.de_DE,
}

export type Market = {
    slug: MarketSlug;
    code: MarketCode;
    language: MarketLanguage;
    name: string;
    iso: MarketIso;
    currency: string;
    countryCode: string;
    operation: number;
};

export const ukMarket: Market = {
    slug: MarketSlug.EN_UK,
    code: MarketCode.EN_UK,
    language: MarketLanguage.EN,
    name: 'United Kingdom',
    iso: MarketIso.en_GB,
    currency: 'GBP',
    countryCode: 'GB',
    operation: 1,
};

export const usMarket: Market = {
    slug: MarketSlug.EN_US,
    code: MarketCode.EN_US,
    name: 'United States',
    language: MarketLanguage.EN,
    iso: MarketIso.en_US,
    currency: 'USD',
    countryCode: 'US',
    operation: 2,
};

export const deMarket: Market = {
    slug: MarketSlug.DE_DE,
    code: MarketCode.DE_DE,
    language: MarketLanguage.DE,
    name: 'Germany',
    iso: MarketIso.de_DE,
    currency: 'EUR',
    countryCode: 'DE',
    operation: 3,
};

export const frMarket: Market = {
    slug: MarketSlug.FR_FR,
    code: MarketCode.FR_FR,
    language: MarketLanguage.FR,
    name: 'France',
    iso: MarketIso.fr_FR,
    currency: 'EUR',
    countryCode: 'FR',
    operation: 3,
};

export const euMarket: Market = {
    slug: MarketSlug.EN_EU,
    code: MarketCode.EN_EU,
    language: MarketLanguage.EN,
    name: 'Europe',
    iso: MarketIso.en_EU,
    currency: 'EUR',
    countryCode: 'EU',
    operation: 3,
};

export const nlMarket: Market = {
    slug: MarketSlug.NL_NL,
    code: MarketCode.NL_NL,
    language: MarketLanguage.NL,
    name: 'Netherlands',
    iso: MarketIso.nl_NL,
    currency: 'EUR',
    countryCode: 'NL',
    operation: 3,
};

export const defaultMarket = ukMarket;

export const englishSpeakingMarkets = [
    MarketCode.EN_UK,
    MarketCode.EN_US,
    MarketCode.EN_EU,
    MarketCode.EN_IE,
];

export const europeanMarkets = [
    MarketCode.FR_FR,
    MarketCode.DE_DE,
    MarketCode.EN_EU,
    MarketCode.NL_NL,
    MarketCode.DE_AT,
    MarketCode.EN_IE,
    MarketCode.FR_BE,
    MarketCode.NL_BE,
    MarketCode.IT_IT,
    MarketCode.ES_ES,
    MarketCode.PT_PT,
];

export const marketsWithFaultyItems = [
    MarketCode.EN_UK,
    MarketCode.EN_US,
    MarketCode.EN_EU,
    MarketCode.EN_IE,
];

/**
 * Market data structure returned from config
 */
export type MarketConfig = Market & {
    publiclyVisible: boolean;
};

/**
 * Operation data structure returned from config
 */
export type OperationFixture = {
    name: string;
    id: number;
    hqAddress: {
        town: string;
    };
};

export type AvailableMarket = Pick<
    Market,
    'name' | 'slug' | 'code' | 'iso' | 'currency' | 'language'
>;

export const MpbxMarketFallback = {
    [MarketSlug.DE_AT]: MarketSlug.DE_DE,
    [MarketSlug.EN_IE]: MarketSlug.EN_EU,
    [MarketSlug.FR_BE]: MarketSlug.FR_FR,
    [MarketSlug.NL_BE]: MarketSlug.NL_NL,
};

export const primaryMarketInBelgium = MarketCode.NL_BE;

// key is the primary market of the country
// the value is a list of the other markets in the country
export const primaryMarketsInMultiMarketCountry = {
    [primaryMarketInBelgium]: [MarketCode.FR_BE],
};
